import React from 'react';
import PropTypes from 'prop-types';
import { Grid, TextField, Typography, Card, Box, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import HotelIcon from '@mui/icons-material/Hotel';
import PersonIcon from '@mui/icons-material/Person';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import { QuotationRoomDistributionFilter } from '../../../types';
import { regexOnlyNumberInt } from '../../../helpers';

const useStyles = makeStyles((theme: any) => ({
	sectionCard: {
		backgroundColor: 'white',
		borderRadius: theme.spacing(1.5),
		padding: theme.spacing(2.5),
		marginBottom: theme.spacing(2),
		border: '1px solid rgba(0,0,0,0.05)',
		boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
	},
	sectionTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
		fontWeight: 'bold',
		color: theme.palette.primary.main,
		fontSize: '1rem',
	},
	inputField: {
		'& .MuiOutlinedInput-root': {
			borderRadius: theme.spacing(1.5),
			backgroundColor: '#f8f9fa',
			transition: 'all 0.3s ease',
			'&:hover': {
				backgroundColor: '#fff',
				boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
			},
			'&.Mui-focused': {
				backgroundColor: '#fff',
				boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
			},
		},
	},
	roomCard: {
		backgroundColor: '#fff3e0',
		border: '1px solid #ffcc02',
		borderRadius: theme.spacing(1.5),
		padding: theme.spacing(2),
		marginBottom: theme.spacing(1.5),
		transition: 'all 0.3s ease',
		'&:hover': {
			boxShadow: '0 4px 12px rgba(255, 204, 2, 0.2)',
		},
	},
	roomTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
		fontWeight: 'bold',
		color: '#e65100',
	},
	roomControls: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	roomCounterButton: {
		borderRadius: '50%',
		minWidth: '40px',
		height: '40px',
		backgroundColor: theme.palette.primary.main,
		color: 'white',
		'&:hover': {
			backgroundColor: theme.palette.primary.dark,
			transform: 'scale(1.05)',
		},
		transition: 'all 0.3s ease',
	},
	roomCountDisplay: {
		padding: theme.spacing(1, 2),
		backgroundColor: 'white',
		borderRadius: theme.spacing(1),
		border: '1px solid #ddd',
		minWidth: '60px',
		textAlign: 'center',
		fontWeight: 'bold',
		fontSize: '1.1rem',
	},
}));

interface RoomConfigurationSectionProps {
	roomDistributions?: QuotationRoomDistributionFilter[];
	onRoomNumberChange: (value: number) => void;
	onAdultsChange: (value: number, index: number) => void;
	onChildrenChange: (value: number | null, index: number) => void;
	onInfantsChange: (value: number | null, index: number) => void;
}

export const RoomConfigurationSection: React.FC<RoomConfigurationSectionProps> = ({
	roomDistributions,
	onRoomNumberChange,
	onAdultsChange,
	onChildrenChange,
	onInfantsChange,
}) => {
	const classes = useStyles();

	const handleDecreaseRooms = () => {
		const currentLength = roomDistributions?.length || 0;
		onRoomNumberChange(currentLength > 1 ? currentLength - 1 : 0);
	};

	const handleIncreaseRooms = () => {
		const currentLength = roomDistributions?.length || 0;
		onRoomNumberChange(currentLength + 1);
	};

	return (
		<Card className={classes.sectionCard}>
			<Typography className={classes.sectionTitle}>
				<HotelIcon />
				Configuración de Habitaciones
			</Typography>

			<Box className={classes.roomControls}>
				<Typography variant="body1" fontWeight="medium">
					Cantidad de habitaciones:
				</Typography>
				<IconButton className={classes.roomCounterButton} size="small" onClick={handleDecreaseRooms}>
					<RemoveIcon />
				</IconButton>
				<Box className={classes.roomCountDisplay}>{roomDistributions?.length || 0}</Box>
				<IconButton className={classes.roomCounterButton} size="small" onClick={handleIncreaseRooms}>
					<AddIcon />
				</IconButton>
			</Box>

			{roomDistributions && roomDistributions.length > 0 && (
				<Grid container spacing={2}>
					{roomDistributions.map((room, index) => (
						<Grid item xs={12} md={6} lg={4} key={`room-${index}-filters`}>
							<Box className={classes.roomCard}>
								<Typography className={classes.roomTitle}>
									<PersonIcon />
									Habitación {index + 1}
								</Typography>
								<Grid container spacing={2}>
									<Grid item xs={12}>
										<TextField
											fullWidth
											size="small"
											className={classes.inputField}
											variant="outlined"
											label="Adultos"
											type="number"
											onChange={({ target: { value } }) => {
												if (regexOnlyNumberInt.test(value) && Number(value) >= 0) {
													onAdultsChange(Number(value), index);
												}
											}}
											value={room.totalAdults || 0}
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											fullWidth
											size="small"
											className={classes.inputField}
											variant="outlined"
											label="Niños"
											onChange={({ target: { value } }) => {
												if (regexOnlyNumberInt.test(value) && Number(value) >= 0) {
													onChildrenChange(Number(value), index);
												} else if (value === '') {
													onChildrenChange(null, index);
												}
											}}
											value={room.totalChildren !== null && room.totalChildren === 0 ? 0 : room.totalChildren || ''}
										/>
									</Grid>
									<Grid item xs={6}>
										<TextField
											fullWidth
											size="small"
											className={classes.inputField}
											variant="outlined"
											label="Infantes"
											onChange={({ target: { value } }) => {
												if (regexOnlyNumberInt.test(value) && Number(value) >= 0) {
													onInfantsChange(Number(value), index);
												} else if (value === '') {
													onInfantsChange(null, index);
												}
											}}
											value={room.totalInfants !== null && room.totalInfants === 0 ? 0 : room.totalInfants || ''}
										/>
									</Grid>
								</Grid>
							</Box>
						</Grid>
					))}
				</Grid>
			)}
		</Card>
	);
};

RoomConfigurationSection.propTypes = {
	roomDistributions: PropTypes.array,
	onRoomNumberChange: PropTypes.func.isRequired,
	onAdultsChange: PropTypes.func.isRequired,
	onChildrenChange: PropTypes.func.isRequired,
	onInfantsChange: PropTypes.func.isRequired,
};
